import { sizeValue } from './tracksModel';
import { SIZE_SPAN } from './useCountModel';

/**
 * What the count panel's info button says about the sizes: what was measured and how far to trust it, then what the
 * size filter keeps. One sentence per state, so the popover never repeats or contradicts itself; the panel shows
 * none of it inline (the Keep line and the "N hidden" count are the visible status).
 */

const metres = v => `${sizeValue(v, 'm')} m`;

// what the sizes are (the panel's histogram, useCountModel.hist) and how far to trust them
// calves: calf-sized is told apart for reindeer only (the size cut was measured on herds)
const measured = (hist, calves) => {
	// animals have a body; a car or a person has a length
	const body = calves ? 'Body length' : 'Length';
	const calf = calves ? ['Calf-sized: small for the herd at the same distance.'] : [];
	if (hist.unit !== 'm') {
		const why = hist.flightWithoutHeight
			? 'the flight data has no height above the ground to give metres'
			: 'this video has no flight data to give metres';
		return [`${body} in pixels: ${why}, and something far away looks small.`, ...calf];
	}
	// a track seen with the camera tilted is measured across the image (flightModel.bodySizes)
	const what = hist.allAcross
		? `${calves ? 'Body width' : 'Width'} across the image, in metres on the ground (the camera is tilted)`
		: hist.across
			? `${body} in metres on the ground, or the width across the image where the camera was tilted`
			: `${body} in metres on the ground`;
	const height =
		hist.heightRef === 'takeoff'
			? ', from the flight height above the take-off point (estimate)'
			: ' (estimate)';
	return [`${what}${height}.`, ...calf];
};

// what the size filter keeps (SizeFilter's props: unit, across, groups); nothing without a filter
const kept = (filter, subject) => {
	const rows = (filter?.groups || []).filter(g => g.values?.length);
	if (!rows.length) return [];
	const out = [];
	if (filter.unit === 'm') {
		// a kind's own name: a one-row filter of a several-kind video (the others had nothing measured) is that kind's
		const nameOf = g => g.kind || subject;
		const usual = rows.flatMap(g => (g.usual || []).map(metres));
		if (!usual.length)
			out.push(
				`No usual size is known for ${rows.map(nameOf).join(' or ')}, so there is no usual range to keep.`
			);
		else if (rows.length > 1)
			out.push(
				`Each kind with a known usual size can keep it ÷ and × ${SIZE_SPAN}, to leave out what is far off.`
			);
		else
			out.push(
				`The usual size of ${nameOf(rows[0])} is ${usual.join(', ')}; keeping it ÷ and × ${SIZE_SPAN} leaves out what is far off.`
			);
		// only the usual size drops its lower limit for a tilted track; a range set by hand applies as set
		if (filter.across && usual.length)
			out.push(
				'Seen with the camera tilted, a size is the width across the image, so the usual size keeps only its upper limit there. A range you set applies as set.'
			);
	}
	// the page's PDF report is made from the page count (reportData), so a kept range reaches it too
	out.push(
		'A range you keep leaves tracks out of this count and of the PDF report; the analysis itself counted every size.'
	);
	return out;
};

/** The paragraphs for the "Size of what was counted" info button, as plain strings. */
const sizeHelp = ({ hist, calves = false, filter = null, subject = '' }) => [
	...measured(hist, calves),
	...kept(filter, subject),
];

export default sizeHelp;
